import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useUsers from './useUsers';
import usePlaylists from './usePlaylists';
import useGroups from './useGroups';
import useCourses from './useCourses';

const WatchDataContext = React.createContext();

function WatchDataProvider({
  handleHistory,
  id,
  isBackOffice,
  isOrganizationManager,
  organizationSelectedId,
  remindAllEventHandler,
  restClient,
  selectOrganizationHandler,
  useGraphQLMutation,
  useLazyGraphQLQuery,
  user,
  view,
  ...props
}) {
  // Global object for error handling
  const [watchDataError, setWatchDataError] = useState(false);
  // Tab selections data
  const [courseSelected, setCourseSelected] = useState(null);
  const [groupSelected, setGroupSelected] = useState(null);
  const [playlistSelected, setPlaylistSelected] = useState(null);

  // Global tab data
  const { usersWatchData, loadingUsersWatchData, fetchUsersWatchData } =
    useUsers({
      setWatchDataError,
      useLazyGraphQLQuery,
    });
  const { allCoursesStats, loadingCoursesWatchData, fetchCoursesWatchData } =
    useCourses({
      setWatchDataError,
      useLazyGraphQLQuery,
    });
  const { organizationGroupsStats, loadingGroups, fetchGroups } = useGroups({
    setWatchDataError,
    useLazyGraphQLQuery,
  });
  const {
    allPlaylistsStats,
    loadingPlaylistsWatchData,
    fetchPlaylistsWatchData,
  } = usePlaylists({
    setWatchDataError,
    useLazyGraphQLQuery,
  });

  const onSelectItem = (itemId) => {
    if (itemId) {
      handleHistory(`view=${view}&id=${itemId}`);
    } else {
      handleHistory(`view=${view}`);
    }
  };

  useEffect(() => {
    if (!id) {
      setCourseSelected(null);
      setGroupSelected(null);
      setPlaylistSelected(null);
    }
  }, [id]);

  const contextValue = {
    // Constant variables
    isBackOffice,
    isOrganizationManager,
    user,
    organizationSelectedId,
    watchDataError,
    setWatchDataError,
    // Global fetching methods and state
    usersWatchData,
    loadingUsersWatchData,
    fetchUsersWatchData,
    allCoursesStats,
    fetchCoursesWatchData,
    loadingCoursesWatchData,
    organizationGroupsStats,
    loadingGroups,
    fetchGroups,
    allPlaylistsStats,
    loadingPlaylistsWatchData,
    fetchPlaylistsWatchData,
    remindAllEventHandler,
    restClient,
    useGraphQLMutation,
    useLazyGraphQLQuery,
    // Tab specific state that needs to be accessed
    courseSelected,
    setCourseSelected,
    groupSelected,
    setGroupSelected,
    playlistSelected,
    setPlaylistSelected,
    // Navigation
    handleHistory,
    id,
    onSelectItem,
    selectOrganizationHandler,
    view,
  };

  return <WatchDataContext.Provider value={contextValue} {...props} />;
}

WatchDataProvider.propTypes = {
  handleHistory: PropTypes.func.isRequired,
  id: PropTypes.string,
  isBackOffice: PropTypes.bool,
  isOrganizationManager: PropTypes.bool,
  organizationSelectedId: PropTypes.string,
  remindAllEventHandler: PropTypes.func, // Just used in client
  restClient: PropTypes.func.isRequired,
  selectOrganizationHandler: PropTypes.func, // Just used in backoffice
  useGraphQLMutation: PropTypes.func.isRequired,
  useLazyGraphQLQuery: PropTypes.func.isRequired,
  user: PropTypes.shape({
    attributes: PropTypes.shape({
      sub: PropTypes.string.isRequired,
    }),
  }).isRequired,
  view: PropTypes.string,
};

WatchDataProvider.defaultProps = {
  isBackOffice: false,
  isOrganizationManager: false,
  organizationSelectedId: null,
};

function useWatchData() {
  const context = React.useContext(WatchDataContext);
  if (!context) {
    throw new Error('useWatchData must be used within a WatchDataProvider');
  }

  return context;
}

export { WatchDataProvider, useWatchData };
